import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Wrench, Layers, Clock, Package, AlertCircle, CheckCircle2, ChevronUp, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Wizard para criar um Lote de Arranjo a partir de peças classificadas.
 * Mostra todas as fontes (anomalias com qtd_arranjo disponível) com rastreabilidade,
 * permite escolher quanto puxar de cada uma, e cria o novo lote ARR.
 */
export default function CriarLoteArranjoModal({ open, onOpenChange, ofId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({ fontes: [], total_disponivel: 0, total_pendente_classificacao: 0 });
  const [selecoes, setSelecoes] = useState({});       // { anomalia_id: qtd }
  const [observacoes, setObservacoes] = useState('');
  const [descricao, setDescricao] = useState('');
  const [tagsActivas, setTagsActivas] = useState(new Set()); // filtros por tag
  const [modo, setModo] = useState('historico'); // 'historico' | 'simples'
  const [quantidadeSimples, setQuantidadeSimples] = useState('');
  const [operadoresOf, setOperadoresOf] = useState([]); // operadores atribuídos à OF
  const [operadoresOutros, setOperadoresOutros] = useState([]); // outros operadores ativos (fora da OF)
  const [showOutrosOperadores, setShowOutrosOperadores] = useState(false);
  const [operadoresSelecionados, setOperadoresSelecionados] = useState(new Set()); // operadores com acesso
  const [etapa, setEtapa] = useState(1); // 1 = Arranjo, 2 = Operações
  const [operacoesOf, setOperacoesOf] = useState([]); // operações da OF (não-ARR)
  const [categoriasEspeciais, setCategoriasEspeciais] = useState([]); // todas categorias especiais (candidatas a Mãe)
  const [categoriaEspecialId, setCategoriaEspecialId] = useState(''); // categoria selecionada para a Mãe
  const [operacoesArr, setOperacoesArr] = useState([]); // operações da categoria especial selecionada
  const [operacaoMaeId, setOperacaoMaeId] = useState(''); // op mãe selecionada
  const [operacoesPermitidas, setOperacoesPermitidas] = useState(new Set()); // operações permitidas (extras, não inclui mãe)
  const [semOperacaoMae, setSemOperacaoMae] = useState(false); // toggle: peças já arranjadas fora — pular operação mãe

  useEffect(() => {
    if (!open || !ofId) return;
    setEtapa(1);
    setOperacaoMaeId('');
    setSemOperacaoMae(false);
    
    // Carregar OF + mapa global de operações + categorias para hidratar tudo num só passo
    Promise.all([
      axios.get(`${API}/ofs/${ofId}`),
      axios.get(`${API}/operacoes`),
      axios.get(`${API}/categorias`)
    ])
      .then(([resOf, resOps, resCats]) => {
        const allOps = Array.isArray(resOps.data) ? resOps.data : [];
        const opsMap = new Map(allOps.map(o => [o.id, o]));
        const allCats = Array.isArray(resCats.data) ? resCats.data : [];
        const catsMap = new Map(allCats.map(c => [c.id, c]));
        const opsAtrib = resOf.data?.operacoes_atribuidas || resOf.data?.operacoes || [];
        const opsBrutas = opsAtrib.map(o => {
          const id = o.operacao_id || o.id;
          const ref = opsMap.get(id) || {};
          // Resolver categoria através de categoria_id → catálogo de categorias
          const catId = o.categoria_id || ref.categoria_id || '';
          const catRef = catId ? (catsMap.get(catId) || {}) : {};
          return {
            id,
            codigo: o.codigo || o.operacao_codigo || ref.codigo || '',
            nome: o.nome || o.operacao_nome || ref.nome || '',
            ordem: o.ordem || 0,
            is_contagem: !!(o.is_contagem || ref.is_contagem),
            categoria_codigo: o.categoria_codigo || ref.categoria_codigo || catRef.codigo || '',
            categoria_nome: o.categoria_nome || ref.categoria_nome || catRef.nome || '',
            categoria_cor: catRef.cor || '#94a3b8',
            categoria_ordem: catRef.ordem ?? 999,
          };
        }).filter(o => o.id);
        opsBrutas.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
        // Excluir ops da categoria ARR — essas vão para Bloco A
        const opsNaoArr = opsBrutas.filter(o => o.categoria_codigo !== 'ARR');
        setOperacoesOf(opsNaoArr);
        setOperacoesPermitidas(new Set(opsNaoArr.map(o => o.id)));
      })
      .catch(() => { setOperacoesOf([]); setOperacoesPermitidas(new Set()); });

    // 2) Carregar categorias especiais (candidatas a operação Mãe). Reutiliza-se quando possível.
    axios.get(`${API}/categorias`)
      .then(res => {
        const especiais = (Array.isArray(res.data) ? res.data : []).filter(c => c.especial);
        especiais.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
        setCategoriasEspeciais(especiais);
        // Selecionar ARR por defeito se existir, senão a primeira especial
        const arr = especiais.find(c => c.codigo === 'ARR');
        const defCat = arr || especiais[0] || null;
        setCategoriaEspecialId(defCat ? defCat.id : '');
      })
      .catch(() => { setCategoriasEspeciais([]); setCategoriaEspecialId(''); });
  }, [open, ofId]);

  // Carregar operações da categoria especial selecionada (via /categorias/{id}/operacoes
  // que respeita operacoes_ids, em vez de filtrar por categoria_id que pode estar a None)
  useEffect(() => {
    if (!open || !categoriaEspecialId) { setOperacoesArr([]); setOperacaoMaeId(''); return; }
    axios.get(`${API}/categorias/${categoriaEspecialId}/operacoes`)
      .then(res => {
        const ops = (res.data?.operacoes || []).map(o => ({
          id: o.id,
          codigo: o.codigo || '',
          nome: o.nome || '',
          is_contagem: !!o.is_contagem,
        }));
        setOperacoesArr(ops);
        // Reset mãe se já não está disponível
        setOperacaoMaeId(prev => ops.find(o => o.id === prev) ? prev : '');
      })
      .catch(() => setOperacoesArr([]));
  }, [open, categoriaEspecialId]);

  useEffect(() => {
    if (!open || !ofId) return;
    setLoading(true);
    axios.get(`${API}/anomalias-producao/arranjo-disponivel/${ofId}`)
      .then(res => {
        setData(res.data || { fontes: [], total_disponivel: 0 });
        // Pré-selecciona tudo (sugestão)
        const novo = {};
        (res.data?.fontes || []).forEach(f => { novo[f.anomalia_id] = f.qtd_disponivel; });
        setSelecoes(novo);
      })
      .catch(() => toast.error('Erro ao carregar peças disponíveis'))
      .finally(() => setLoading(false));
    // Carregar operadores atribuídos à OF
    axios.get(`${API}/ofs/${ofId}/operadores-atribuidos`)
      .then(res => {
        const ops = res.data?.operadores || [];
        setOperadoresOf(ops);
        // Pré-seleciona TODOS por defeito (seguro — acesso liberado)
        setOperadoresSelecionados(new Set(ops.map(o => o.id)));
        // Carregar todos os operadores ativos para permitir adicionar fora da OF
        const idsOf = new Set(ops.map(o => o.id));
        axios.get(`${API}/operadores`, { params: { perfil: 'operador', ativo: true } })
          .then(r2 => {
            const todos = (r2.data || []).map(o => ({
              id: o.id,
              nome: o.nome_completo || o.nome || o.username,
            }));
            setOperadoresOutros(todos.filter(o => !idsOf.has(o.id)));
          })
          .catch(() => setOperadoresOutros([]));
      })
      .catch(() => { setOperadoresOf([]); setOperadoresOutros([]); });
    setObservacoes('');
    setDescricao('');
    setTagsActivas(new Set());
    setQuantidadeSimples('');
  }, [open, ofId]);

  // Verificar se há alterações "não guardadas"
  const hasChanges = useMemo(() => {
    if (modo === 'simples') return (parseInt(quantidadeSimples) || 0) > 0 || !!observacoes;
    const sel = Object.values(selecoes).some(v => parseInt(v) > 0);
    return sel || !!observacoes;
  }, [modo, quantidadeSimples, selecoes, observacoes]);

  // Proteção ao fechar
  const handleClose = (next) => {
    if (!next && hasChanges) {
      const ok = window.confirm('Tem alterações não guardadas. Deseja sair sem criar o lote?');
      if (!ok) return;
    }
    onOpenChange(next);
  };

  // Tags únicas das fontes
  const todasTags = useMemo(() => {
    const s = new Set();
    (data.fontes || []).forEach(f => (f.tags || []).forEach(t => s.add(t)));
    return [...s];
  }, [data.fontes]);

  // Fontes filtradas por tags activas (intersecção: tem PELO MENOS UMA das tags activas)
  const fontesFiltradas = useMemo(() => {
    if (tagsActivas.size === 0) return data.fontes || [];
    return (data.fontes || []).filter(f => (f.tags || []).some(t => tagsActivas.has(t)));
  }, [data.fontes, tagsActivas]);

  const toggleTag = (t) => {
    setTagsActivas(prev => {
      const novo = new Set(prev);
      if (novo.has(t)) novo.delete(t);
      else novo.add(t);
      return novo;
    });
  };

  const totalSelecionado = useMemo(
    () => Object.values(selecoes).reduce((acc, n) => acc + (parseInt(n) || 0), 0),
    [selecoes]
  );

  const tempoTotalRecuperar = useMemo(() => {
    return (data.fontes || []).reduce((acc, f) => {
      const qtd = parseInt(selecoes[f.anomalia_id]) || 0;
      if (qtd === 0) return acc;
      return acc + (f.rastreabilidade?.tempo_restante_min || 0) * qtd;
    }, 0);
  }, [data.fontes, selecoes]);

  const setQtd = (anomaliaId, max, val) => {
    const n = Math.max(0, Math.min(max, parseInt(val) || 0));
    setSelecoes(s => ({ ...s, [anomaliaId]: n }));
  };

  const tudoMax = () => {
    const novo = {};
    (fontesFiltradas || []).forEach(f => { novo[f.anomalia_id] = f.qtd_disponivel; });
    setSelecoes({ ...selecoes, ...novo });
  };

  const limparTudo = () => setSelecoes({});

  const handleConfirm = async () => {
    let fontes;
    if (modo === 'simples') {
      const qtd = parseInt(quantidadeSimples) || 0;
      const maxDisp = data.total_disponivel || 0;
      if (qtd <= 0) { toast.error('Indique uma quantidade > 0'); return; }
      if (qtd > maxDisp) { toast.error(`Máximo disponível: ${maxDisp}`); return; }
      // Distribuir qtd pelas fontes na ordem (FIFO) — sem mostrar rastreabilidade detalhada
      fontes = [];
      let rest = qtd;
      for (const f of (data.fontes || [])) {
        if (rest <= 0) break;
        const take = Math.min(rest, f.qtd_disponivel || 0);
        if (take > 0) { fontes.push({ anomalia_id: f.anomalia_id, qtd: take }); rest -= take; }
      }
    } else {
      fontes = Object.entries(selecoes)
        .map(([anomalia_id, qtd]) => ({ anomalia_id, qtd: parseInt(qtd) || 0 }))
        .filter(f => f.qtd > 0);
    }
    if (fontes.length === 0) {
      toast.error('Seleccione pelo menos 1 peça');
      return;
    }
    if (!descricao.trim()) {
      toast.error('Indique a descrição do trabalho a fazer (obrigatório)');
      return;
    }
    if (!semOperacaoMae && !operacaoMaeId) {
      toast.error('Selecione uma operação mãe (ou marque "Sem operação mãe")');
      setEtapa(2);
      return;
    }
    if (semOperacaoMae && operacoesPermitidas.size === 0) {
      toast.error('Selecione pelo menos uma operação adicional (sem operação mãe, é necessária pelo menos 1 op B)');
      setEtapa(2);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/ofs/${ofId}/lotes/arranjo`, {
        fontes,
        descricao: descricao.trim(),
        observacoes: observacoes || undefined,
        operadores_acesso: [...operadoresSelecionados],
        operacao_mae_id: semOperacaoMae ? undefined : (operacaoMaeId || undefined),
        operacoes_permitidas: [...operacoesPermitidas],
      });
      toast.success(`Lote ${res.data.lote.codigo_lote} criado com ${res.data.quantidade_total} peças`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar lote');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="criar-lote-arranjo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-red-600" />
            Criar Lote de Arranjo
          </DialogTitle>
          <DialogDescription>
            Seleccione as peças classificadas como Arranjo a incluir no novo lote. As peças mantêm rastreabilidade da operação onde a anomalia ocorreu.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper — 2 etapas */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <button
            type="button"
            onClick={() => setEtapa(1)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition ${etapa === 1 ? 'bg-red-600 text-white shadow' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
            data-testid="etapa-arranjo-btn"
          >
            <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-extrabold ${etapa === 1 ? 'bg-white/20 text-white' : 'bg-stone-300 text-stone-700'}`}>1</span>
            Arranjo
          </button>
          <span className="text-stone-300">→</span>
          <button
            type="button"
            onClick={() => setEtapa(2)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition ${etapa === 2 ? 'bg-red-600 text-white shadow' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
            data-testid="etapa-operacoes-btn"
          >
            <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-extrabold ${etapa === 2 ? 'bg-white/20 text-white' : 'bg-stone-300 text-stone-700'}`}>2</span>
            Operações
            <span className="text-[10px] opacity-80 font-medium">({operacoesPermitidas.size}/{operacoesOf.length})</span>
          </button>
        </div>

        {/* ETAPA 2 — Operações permitidas neste Lote ARR */}
        {etapa === 2 && (
          <div className="space-y-4" data-testid="etapa-operacoes-content">
            {/* BLOCO A — Operação Mãe (categoria especial) */}
            <div className={`rounded-lg border-2 p-3 transition ${semOperacaoMae ? 'border-stone-200 bg-stone-50' : 'border-rose-300 bg-rose-50'}`}>
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded text-white font-extrabold ${semOperacaoMae ? 'bg-stone-400' : 'bg-rose-600'}`}>A</span>
                  <p className={`font-bold text-sm ${semOperacaoMae ? 'text-stone-500 line-through' : 'text-rose-900'}`}>
                    Operação Mãe — {semOperacaoMae ? 'desactivada' : 'obrigatória'}
                  </p>
                </div>
                {/* Toggle: Sem operação mãe */}
                <label className="flex items-center gap-2 cursor-pointer text-xs select-none" data-testid="toggle-sem-operacao-mae">
                  <input
                    type="checkbox"
                    checked={semOperacaoMae}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setSemOperacaoMae(v);
                      if (v) {
                        // limpar seleção da mãe quando o gestor opta por pular
                        setOperacaoMaeId('');
                      }
                    }}
                    className="h-4 w-4 accent-rose-600"
                  />
                  <span className="font-semibold text-stone-700">
                    Sem operação mãe
                    <span className="ml-1 text-stone-500 font-normal">(arranjado externamente)</span>
                  </span>
                </label>
              </div>
              {semOperacaoMae ? (
                <div className="rounded border border-amber-300 bg-amber-50 p-2.5 text-[11px] text-amber-900 leading-snug">
                  <strong>As peças já foram arranjadas (ex: numa empresa externa).</strong> O lote será criado <strong>sem operação mãe</strong>. As operações adicionais selecionadas em <strong>B</strong> ficam disponíveis imediatamente para o operador.
                </div>
              ) : (
              <>
              <p className="text-[11px] text-rose-800/80 mb-2">É a primeira operação executada no lote. Tem de ser concluída antes de qualquer operação adicional. Se for de contagem, o resultado define a quantidade para as operações seguintes.</p>

              {/* Seletor de Categoria Especial */}
              <div className="mb-3">
                <label className="block text-[11px] font-semibold text-rose-900 mb-1">Categoria especial</label>
                {categoriasEspeciais.length === 0 ? (
                  <p className="text-xs text-rose-700 italic py-2">Nenhuma categoria especial definida. Cria uma em <strong>/gestor/categorias</strong> e marca-a como "especial".</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {categoriasEspeciais.map(cat => {
                      const sel = categoriaEspecialId === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setCategoriaEspecialId(cat.id)}
                          className={`px-2.5 py-1 rounded-full text-xs font-bold border-2 transition ${sel ? 'text-white border-rose-700 shadow' : 'bg-white border-stone-200 hover:border-rose-400 text-stone-700'}`}
                          style={sel ? { background: cat.cor || '#dc2626' } : {}}
                          data-testid={`cat-especial-${cat.codigo}`}
                        >
                          {cat.nome} <span className="opacity-70 text-[10px]">{cat.codigo}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {operacoesArr.length === 0 ? (
                <p className="text-xs text-rose-700 italic py-3 text-center">Nenhuma operação na categoria selecionada. Adiciona operações em <strong>/gestor/categorias</strong> (clica para editar a categoria).</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto">
                  {operacoesArr.map(op => {
                    const sel = operacaoMaeId === op.id;
                    return (
                      <button
                        key={op.id}
                        type="button"
                        onClick={() => setOperacaoMaeId(op.id)}
                        className={`text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 transition ${sel ? 'bg-rose-600 border-rose-700 text-white shadow' : 'bg-white border-stone-200 hover:border-rose-400'}`}
                        data-testid={`op-mae-${op.id}`}
                      >
                        <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full border-2 flex-shrink-0 ${sel ? 'bg-white border-white' : 'border-stone-300'}`}>
                          {sel && <span className="block h-2.5 w-2.5 rounded-full bg-rose-600"/>}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${sel ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-700'}`}>{op.codigo}</span>
                            {op.is_contagem && (
                              <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${sel ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-700'}`}>CONTAGEM</span>
                            )}
                          </div>
                          <p className={`text-sm font-semibold truncate mt-0.5 ${sel ? 'text-white' : 'text-stone-800'}`}>{op.nome}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              </>
              )}
            </div>
            <div className="rounded-lg border-2 border-stone-200 bg-stone-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-700 text-white font-extrabold">B</span>
                  <p className="font-bold text-sm text-stone-800">Operações adicionais (da OF)</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setOperacoesPermitidas(new Set(operacoesOf.map(o => o.id)))} className="h-7 text-xs">Todas</Button>
                  <Button size="sm" variant="ghost" onClick={() => setOperacoesPermitidas(new Set())} className="h-7 text-xs">Nenhuma</Button>
                </div>
              </div>
              <p className="text-[11px] text-stone-600 mb-2">{semOperacaoMae ? 'Selecciona quais operações da OF (já atribuídas) ficam disponíveis ao operador. Como não há operação mãe, todas estas operações ficam disponíveis imediatamente.' : 'Selecciona quais operações da OF (já atribuídas) ficam disponíveis após a operação mãe estar concluída. Estas só podem ser iniciadas DEPOIS da mãe. Agrupadas por categoria.'}</p>
              {operacoesOf.length === 0 ? (
                <p className="text-center text-sm text-stone-500 py-4">Nenhuma operação adicional disponível</p>
              ) : (() => {
                const grupos = {};
                const grupoMeta = {};
                operacoesOf.forEach(op => {
                  const cat = op.categoria_codigo || 'sem-categoria';
                  if (!grupos[cat]) {
                    grupos[cat] = [];
                    grupoMeta[cat] = {
                      nome: op.categoria_nome || (cat === 'sem-categoria' ? 'Sem categoria' : cat),
                      cor: op.categoria_cor || '#94a3b8',
                      ordem: op.categoria_ordem ?? 999,
                    };
                  }
                  grupos[cat].push(op);
                });
                const catKeys = Object.keys(grupos).sort((a, b) => {
                  const oa = grupoMeta[a].ordem;
                  const ob = grupoMeta[b].ordem;
                  if (oa !== ob) return oa - ob;
                  return a.localeCompare(b);
                });
                return (
                  <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                    {catKeys.map(catCodigo => {
                      const opsCat = grupos[catCodigo];
                      const meta = grupoMeta[catCodigo];
                      const idsCat = opsCat.map(o => o.id);
                      const todasMarcadas = idsCat.every(id => operacoesPermitidas.has(id));
                      const algumasMarcadas = idsCat.some(id => operacoesPermitidas.has(id));
                      const nMarcadas = idsCat.filter(id => operacoesPermitidas.has(id)).length;
                      const toggleCat = (marcar) => {
                        const novo = new Set(operacoesPermitidas);
                        if (marcar) idsCat.forEach(id => novo.add(id));
                        else idsCat.forEach(id => novo.delete(id));
                        setOperacoesPermitidas(novo);
                      };
                      return (
                        <div
                          key={catCodigo}
                          className="rounded-lg border-2 bg-white p-2.5"
                          style={{ borderColor: `${meta.cor}40` }}
                          data-testid={`grupo-categoria-${catCodigo}`}
                        >
                          <div className="flex items-center justify-between mb-2 pb-1.5 border-b" style={{ borderColor: `${meta.cor}30` }}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-[10px] px-2 py-0.5 rounded font-extrabold tracking-wide text-white flex-shrink-0" style={{ backgroundColor: meta.cor }}>
                                {catCodigo === 'sem-categoria' ? '—' : catCodigo}
                              </span>
                              <span className="text-xs font-bold text-stone-700 truncate">{meta.nome}</span>
                              <span className="text-[10px] text-stone-500 flex-shrink-0">({nMarcadas}/{idsCat.length})</span>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => toggleCat(true)}
                                disabled={todasMarcadas}
                                className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-40 font-semibold"
                                data-testid={`cat-todas-${catCodigo}`}
                              >Todas</button>
                              <button
                                type="button"
                                onClick={() => toggleCat(false)}
                                disabled={!algumasMarcadas}
                                className="text-[10px] px-2 py-0.5 rounded bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-40 font-semibold"
                                data-testid={`cat-nenhuma-${catCodigo}`}
                              >Nenhuma</button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {opsCat.map(op => {
                              const sel = operacoesPermitidas.has(op.id);
                              return (
                                <button
                                  key={op.id}
                                  type="button"
                                  onClick={() => {
                                    const novo = new Set(operacoesPermitidas);
                                    if (sel) novo.delete(op.id); else novo.add(op.id);
                                    setOperacoesPermitidas(novo);
                                  }}
                                  className={`text-left flex items-center gap-3 px-3 py-2 rounded-lg border-2 transition ${sel ? 'bg-emerald-50 border-emerald-400' : 'bg-white border-stone-200 hover:border-stone-400'}`}
                                  data-testid={`op-permitida-${op.id}`}
                                >
                                  <span className={`inline-flex items-center justify-center h-5 w-5 rounded border-2 flex-shrink-0 ${sel ? 'bg-emerald-600 border-emerald-700 text-white' : 'border-stone-300'}`}>
                                    {sel && <span className="text-[10px] font-extrabold">✓</span>}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">{op.codigo}</span>
                                    </div>
                                    <p className="text-sm font-semibold text-stone-800 truncate mt-0.5">{op.nome}</p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-stone-200">
              <Button variant="outline" type="button" onClick={() => setEtapa(1)} data-testid="voltar-etapa-1">← Voltar a Arranjo</Button>
              <Button onClick={handleConfirm} disabled={submitting || operadoresSelecionados.size === 0 || (!semOperacaoMae && !operacaoMaeId) || (semOperacaoMae && operacoesPermitidas.size === 0)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="criar-lote-final-btn">
                {submitting ? 'A criar...' : 'Criar Lote ARR'}
              </Button>
            </div>
          </div>
        )}

        {/* ETAPA 1 — Arranjo (peças, descrição, operadores, tags) */}
        {etapa === 1 && (<>

        {/* Toggle Modo: Com histórico / Simples */}
        <div className="inline-flex rounded-lg bg-stone-100 p-1 gap-1 self-start">
          <button
            type="button"
            onClick={() => setModo('historico')}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition ${modo === 'historico' ? 'bg-red-600 text-white shadow-sm' : 'text-stone-600 hover:text-stone-900'}`}
            data-testid="modo-historico-btn"
          >
            Com Histórico (rastreabilidade detalhada)
          </button>
          <button
            type="button"
            onClick={() => setModo('simples')}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition ${modo === 'simples' ? 'bg-red-600 text-white shadow-sm' : 'text-stone-600 hover:text-stone-900'}`}
            data-testid="modo-simples-btn"
          >
            Simples (só quantidade)
          </button>
        </div>

        {modo === 'simples' && (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50/40" data-testid="modo-simples-panel">
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-4 w-4 text-red-600" />
              <h3 className="font-semibold text-red-700 text-sm">Criação Rápida — Sem rastreabilidade</h3>
            </div>
            <p className="text-xs text-stone-600 mb-3">
              Use este modo quando não precisa de detalhar origem por peça. A rastreabilidade é guardada automaticamente em ordem (FIFO).
            </p>
            <div className="flex items-end gap-3">
              <div>
                <label className="text-xs text-stone-500 uppercase">Disponíveis</label>
                <p className="text-3xl font-bold text-red-600">{data.total_disponivel || 0}</p>
              </div>
              <div className="flex-1">
                <label className="text-xs text-stone-500 uppercase block mb-1">Quantidade para o lote</label>
                <Input
                  type="number"
                  min="1"
                  max={data.total_disponivel || 0}
                  value={quantidadeSimples}
                  onChange={(e) => setQuantidadeSimples(e.target.value)}
                  placeholder="Ex: 5"
                  className="h-10 text-lg font-bold"
                  data-testid="quantidade-simples-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setQuantidadeSimples(String(data.total_disponivel || 0))}
                className="h-10"
                data-testid="usar-tudo-simples-btn"
              >
                Usar tudo ({data.total_disponivel || 0})
              </Button>
            </div>
          </div>
        )}

        {modo === 'historico' && (<>

        {/* Resumo */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
            <p className="text-xs text-red-700 uppercase">Disponíveis</p>
            <p className="text-2xl font-bold text-red-700">{data.total_disponivel || 0}</p>
            <p className="text-xs text-red-500">{(data.fontes || []).length} fontes</p>
          </div>
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
            <p className="text-xs text-amber-700 uppercase">Seleccionadas</p>
            <p className="text-2xl font-bold text-amber-700" data-testid="total-selecionado">{totalSelecionado}</p>
            <p className="text-xs text-amber-500">para o novo lote</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
            <p className="text-xs text-blue-700 uppercase">Tempo previsto</p>
            <p className="text-2xl font-bold text-blue-700">{Math.round(tempoTotalRecuperar)}min</p>
            <p className="text-xs text-blue-500">total de recuperação</p>
          </div>
        </div>

        {/* Acções rápidas */}
        <div className="flex items-center justify-between text-sm">
          <div>
            {data.total_pendente_classificacao > 0 ? (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 border border-red-300" data-testid="banner-pendentes-classificacao">
                <AlertCircle className="h-4 w-4 text-red-600 animate-pulse" />
                <span className="text-red-800 font-semibold">
                  ⚠ {data.total_pendente_classificacao} peça{data.total_pendente_classificacao > 1 ? 's' : ''} pendente{data.total_pendente_classificacao > 1 ? 's' : ''} de classificação
                </span>
                <span className="text-xs text-red-600 italic">— classifique-as primeiro para as incluir neste lote</span>
              </div>
            ) : (
              <span className="text-stone-500 text-xs">Todas as peças estão classificadas ✓</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={limparTudo} data-testid="limpar-selecao">Limpar</Button>
            <Button size="sm" variant="outline" onClick={tudoMax} data-testid="selecionar-tudo">Seleccionar tudo</Button>
          </div>
        </div>

        {/* Filtros por Tag */}
        {todasTags.length > 0 && (
          <div className="p-2.5 rounded-lg border border-violet-200 bg-violet-50/40">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase font-semibold text-violet-700">Filtrar por tag:</span>
              {todasTags.map(t => {
                const active = tagsActivas.has(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    className={`px-2 py-0.5 rounded-full text-xs font-medium border transition ${active ? 'bg-violet-600 text-white border-violet-700' : 'bg-white text-violet-700 border-violet-200 hover:bg-violet-100'}`}
                    data-testid={`filtro-tag-${t}`}
                  >
                    {active ? '✓ ' : ''}{t}
                  </button>
                );
              })}
              {tagsActivas.size > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setTagsActivas(new Set())} className="h-6 text-[11px] text-violet-700">
                  Limpar filtros
                </Button>
              )}
            </div>
            {tagsActivas.size > 0 && (
              <p className="text-[11px] text-violet-700/70 mt-1">A mostrar {fontesFiltradas.length} de {data.fontes?.length || 0} fontes (com pelo menos uma tag selecionada).</p>
            )}
          </div>
        )}

        {/* Lista de fontes */}
        <div className="border border-stone-200 rounded-lg overflow-hidden">
          {loading && (
            <div className="text-center py-8 text-stone-400">A carregar peças disponíveis...</div>
          )}
          {!loading && (data.fontes || []).length === 0 && (
            <div className="text-center py-8 text-stone-400">
              Nenhuma peça classificada como arranjo disponível para criar lote.
            </div>
          )}
          {!loading && (data.fontes || []).length > 0 && (
            <table className="w-full text-sm" data-testid="fontes-table">
              <thead className="bg-stone-50 border-b border-stone-200 text-xs uppercase text-stone-600">
                <tr>
                  <th className="px-3 py-2 text-left">Lote Origem</th>
                  <th className="px-3 py-2 text-left">Op. Falhou</th>
                  <th className="px-3 py-2 text-center">Fase</th>
                  <th className="px-3 py-2 text-center">Tempo Rec.</th>
                  <th className="px-3 py-2 text-left">Tags</th>
                  <th className="px-3 py-2 text-right">Disponível</th>
                  <th className="px-3 py-2 text-center">Quantidade</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {fontesFiltradas.map(f => {
                  const rast = f.rastreabilidade || {};
                  const fasePct = rast.ops_total ? Math.round((rast.ops_completadas / rast.ops_total) * 100) : 0;
                  const sel = parseInt(selecoes[f.anomalia_id]) || 0;
                  return (
                    <tr key={f.anomalia_id} className={sel > 0 ? 'bg-amber-50' : ''} data-testid={`fonte-row-${f.anomalia_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-stone-800">{f.lote_codigo || '—'}</div>
                        <div className="text-xs text-stone-500">{f.operador_nome}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-stone-700">{f.operacao_codigo}</div>
                        <div className="text-xs text-stone-500 truncate max-w-[160px]">{f.operacao_nome}</div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className="text-sm font-semibold text-blue-700">{rast.ops_completadas || 0}/{rast.ops_total || 0}</span>
                          <div className="w-12 h-1 bg-stone-200 rounded-full mt-1 overflow-hidden">
                            <div className="h-full bg-blue-500" style={{ width: `${fasePct}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center text-amber-700">
                        <Clock className="h-3 w-3 inline mr-0.5" />
                        {rast.tempo_restante_min || 0}min
                      </td>
                      <td className="px-3 py-2">
                        {(f.tags && f.tags.length > 0) ? (
                          <div className="flex flex-wrap gap-1 max-w-[150px]">
                            {f.tags.map((t, i) => (
                              <span key={i} className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${tagsActivas.has(t) ? 'bg-violet-600 text-white' : 'bg-violet-100 text-violet-700'}`}>{t}</span>
                            ))}
                          </div>
                        ) : <span className="text-stone-300 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-red-700">{f.qtd_disponivel}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => setQtd(f.anomalia_id, f.qtd_disponivel, sel - 1)}
                            disabled={sel <= 0}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Input
                            type="number"
                            min={0}
                            max={f.qtd_disponivel}
                            value={sel}
                            onChange={(e) => setQtd(f.anomalia_id, f.qtd_disponivel, e.target.value)}
                            className="h-8 w-16 text-center"
                            data-testid={`qtd-input-${f.anomalia_id}`}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => setQtd(f.anomalia_id, f.qtd_disponivel, sel + 1)}
                            disabled={sel >= f.qtd_disponivel}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        </>)}

        {/* Operadores com acesso ao lote */}
        <div className="p-3 rounded-lg border border-blue-200 bg-blue-50/40" data-testid="operadores-acesso-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white text-xs font-bold">👤</span>
              <div>
                <h3 className="font-semibold text-blue-800 text-sm">Operadores com Acesso a este Lote</h3>
                <p className="text-[11px] text-blue-700/70">Apenas os operadores seleccionados poderão trabalhar este Lote ARR. Por defeito ficam seleccionados todos os que já estão atribuídos à OF.</p>
              </div>
            </div>
            {(operadoresOf.length > 0 || operadoresOutros.length > 0) && (
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setOperadoresSelecionados(new Set([...operadoresOf.map(o => o.id), ...operadoresOutros.map(o => o.id)]))} className="h-7 text-xs">Todos</Button>
                <Button size="sm" variant="ghost" onClick={() => setOperadoresSelecionados(new Set())} className="h-7 text-xs">Nenhum</Button>
              </div>
            )}
          </div>
          {operadoresOf.length === 0 ? (
            <p className="text-xs text-stone-500 italic">Nenhum operador atribuído a esta OF ainda. Todos poderão aceder ao lote.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {operadoresOf.map(op => {
                const sel = operadoresSelecionados.has(op.id);
                return (
                  <button
                    key={op.id}
                    type="button"
                    onClick={() => {
                      const novo = new Set(operadoresSelecionados);
                      if (sel) novo.delete(op.id); else novo.add(op.id);
                      setOperadoresSelecionados(novo);
                    }}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border-2 transition ${sel ? 'bg-blue-600 text-white border-blue-700 shadow-sm' : 'bg-white text-stone-600 border-stone-300 hover:border-blue-400'}`}
                    data-testid={`operador-chip-${op.id}`}
                  >
                    <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold ${sel ? 'bg-white/20' : 'bg-blue-100 text-blue-700'}`}>
                      {(op.nome || '?').charAt(0).toUpperCase()}
                    </span>
                    {op.nome}
                    {sel && <span className="text-[10px] opacity-80">✓</span>}
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-2 text-[11px] text-blue-700 flex items-center justify-between gap-3 flex-wrap">
            <span><strong>{operadoresSelecionados.size}</strong> de {operadoresOf.length + operadoresOutros.length} operadores seleccionados</span>
            {operadoresOutros.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={() => setShowOutrosOperadores(v => !v)}
                className="h-7 text-xs"
                data-testid="btn-toggle-outros-operadores"
              >
                {showOutrosOperadores ? 'Ocultar' : `+ Adicionar outro operador (${operadoresOutros.length})`}
              </Button>
            )}
          </div>
          {showOutrosOperadores && operadoresOutros.length > 0 && (
            <div className="mt-2 pt-2 border-t border-blue-200">
              <p className="text-[11px] text-blue-700/70 mb-1.5">Operadores fora da OF — selecciona para conceder acesso a este lote</p>
              <div className="flex flex-wrap gap-2">
                {operadoresOutros.map(op => {
                  const sel = operadoresSelecionados.has(op.id);
                  return (
                    <button
                      key={op.id}
                      type="button"
                      onClick={() => {
                        const novo = new Set(operadoresSelecionados);
                        if (sel) novo.delete(op.id); else novo.add(op.id);
                        setOperadoresSelecionados(novo);
                      }}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border-2 border-dashed transition ${sel ? 'bg-blue-600 text-white border-blue-700 shadow-sm' : 'bg-white text-stone-600 border-stone-300 hover:border-blue-400'}`}
                      data-testid={`operador-chip-extra-${op.id}`}
                    >
                      <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold ${sel ? 'bg-white/20' : 'bg-stone-100 text-stone-700'}`}>
                        {(op.nome || '?').charAt(0).toUpperCase()}
                      </span>
                      {op.nome}
                      {sel && <span className="text-[10px] opacity-80">✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Descrição da Operação (obrigatório) */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="lote-arranjo-descricao-section">
          <label className="text-sm font-bold text-red-800 mb-1 flex items-center gap-1">
            Descrição da Operação <span className="text-red-600">*</span>
          </label>
          <p className="text-xs text-red-700/80 mb-2">Este texto será mostrado ao operador como o "trabalho a fazer" no lote.</p>
          <Textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Ex: Refazer costura do bolso esquerdo, retirar fios soltos da bainha…"
            rows={2}
            className={`bg-white border-2 ${descricao.trim() ? 'border-red-200' : 'border-red-400'} focus-visible:ring-red-400`}
            data-testid="lote-arranjo-descricao"
            required
          />
        </div>

        {/* Observações */}
        <div>
          <label className="text-sm font-medium text-stone-700 mb-1 block">Observações internas (opcional)</label>
          <Textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Notas internas sobre este lote (não visíveis ao operador)…"
            rows={2}
            data-testid="lote-arranjo-obs"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-stone-200">
          <div className="text-sm text-stone-600">
            {modo === 'simples' ? (
              (parseInt(quantidadeSimples) || 0) > 0 ? (
                <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                  <Package className="h-4 w-4" />
                  {parseInt(quantidadeSimples)} peças → 1 novo lote ARR (simples)
                </span>
              ) : (
                <span className="text-stone-400">Indique uma quantidade</span>
              )
            ) : totalSelecionado > 0 ? (
              <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                <Package className="h-4 w-4" />
                {totalSelecionado} peças → 1 novo lote ARR
              </span>
            ) : (
              <span className="text-stone-400">Seleccione peças para criar o lote</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleClose(false)}>Cancelar</Button>
            <Button
              onClick={() => setEtapa(2)}
              disabled={modo === 'historico' ? totalSelecionado === 0 : (parseInt(quantidadeSimples) || 0) === 0}
              className="bg-red-600 hover:bg-red-700"
              data-testid="proximo-operacoes-btn"
            >
              Próximo: Operações →
            </Button>
          </div>
        </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
